import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

// ---------- OAuth settings ----------

export type OAuthProviderStatus = {
  provider: 'github' | 'google';
  configured: boolean;
  enabled: boolean;
  client_id_prefix: string | null;
};

export type OAuthSettings = {
  providers: OAuthProviderStatus[];
  callback_urls: { github: string; google: string };
};

export function usePlatformOAuth() {
  return useQuery({
    queryKey: ['platform', 'oauth'],
    queryFn: () => api.get<OAuthSettings>('/api/platform/settings/oauth'),
  });
}

export function useSaveOAuthProvider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      provider: 'github' | 'google';
      client_id: string;
      client_secret: string;
    }) =>
      api.put<{ configured: boolean; provider: string }>(
        `/api/platform/settings/oauth/${args.provider}`,
        { client_id: args.client_id, client_secret: args.client_secret },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'oauth'] }),
  });
}

export function useDisableOAuthProvider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (provider: 'github' | 'google') =>
      api.del<{ disabled: boolean }>(`/api/platform/settings/oauth/${provider}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'oauth'] }),
  });
}

// ---------- General settings ----------

export type GeneralSettings = {
  platform_name?: string;
  support_email?: string;
  tagline?: string;
  google_analytics_id?: string;
};

export function usePlatformGeneral() {
  return useQuery({
    queryKey: ['platform', 'general'],
    queryFn: () => api.get<{ general: GeneralSettings }>('/api/platform/settings/general'),
  });
}

export function useSaveGeneral() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: GeneralSettings) =>
      api.put<{ general: GeneralSettings }>('/api/platform/settings/general', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'general'] }),
  });
}

// ---------- Announcements (admin CRUD) ----------

export type AdminAnnouncement = {
  id: string;
  title: string;
  body_html: string | null;
  severity: 'info' | 'success' | 'warning' | 'danger';
  link_url: string | null;
  link_label: string | null;
  published_at: number;
  expires_at: number | null;
  dismissible: number;
  target_audience: 'all' | 'user' | 'owner';
  pinned: number;
  created_at: number;
};

export type AnnouncementInput = {
  id: string;
  title: string;
  body_html?: string;
  severity?: 'info' | 'success' | 'warning' | 'danger';
  link_url?: string;
  link_label?: string;
  expires_at?: number | null;
  dismissible?: boolean;
  target_audience?: 'all' | 'user' | 'owner';
  pinned?: boolean;
};

export function usePlatformAnnouncements() {
  return useQuery({
    queryKey: ['platform', 'announcements'],
    queryFn: () =>
      api.get<{ announcements: AdminAnnouncement[] }>('/api/platform/announcements'),
  });
}

export function useCreateAnnouncement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AnnouncementInput) =>
      api.post<{ id: string }>('/api/platform/announcements', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform', 'announcements'] });
      qc.invalidateQueries({ queryKey: ['announcements'] });
    },
  });
}

export function useUpdateAnnouncement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; body: Partial<AnnouncementInput> }) =>
      api.put<{ updated: number }>(`/api/platform/announcements/${args.id}`, args.body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform', 'announcements'] });
      qc.invalidateQueries({ queryKey: ['announcements'] });
    },
  });
}

export function useDeleteAnnouncement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.del<{ deleted: boolean }>(`/api/platform/announcements/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform', 'announcements'] });
      qc.invalidateQueries({ queryKey: ['announcements'] });
    },
  });
}

// ---------- Blockchain Explorer keys ----------

export type ExplorerProviderId = 'etherscan_v2' | 'trongrid' | 'helius' | 'toncenter';

export type ExplorerProviderInfo = {
  id: ExplorerProviderId;
  label: string;
  description: string;
  docs_url: string;
  required: boolean;
};

export type ExplorerStatus = {
  provider: ExplorerProviderId;
  configured: boolean;
  key_hint: string | null;
  updated_at: number | null;
};

export function usePlatformExplorers() {
  return useQuery({
    queryKey: ['platform', 'explorers'],
    queryFn: () =>
      api.get<{ providers: ExplorerProviderInfo[]; statuses: ExplorerStatus[] }>(
        '/api/platform/settings/explorers',
      ),
  });
}

export function useSaveExplorerKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { provider: ExplorerProviderId; key: string }) =>
      api.put<{ saved: boolean }>(`/api/platform/settings/explorers/${args.provider}`, {
        key: args.key,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'explorers'] }),
  });
}

export function useRemoveExplorerKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (provider: ExplorerProviderId) =>
      api.del<{ removed: boolean }>(`/api/platform/settings/explorers/${provider}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'explorers'] }),
  });
}

// ---------- Users ----------

export type PlatformUser = {
  id: string;
  email: string;
  full_name: string | null;
  avatar_url: string | null;
  platform_role: 'owner' | 'user';
  email_verified_at: number | null;
  created_at: number;
  membership_count: number;
};

export function usePlatformUsers(params: { search?: string; role?: 'owner' | 'user' } = {}) {
  return useQuery({
    queryKey: ['platform', 'users', params],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (params.search) qs.set('search', params.search);
      if (params.role) qs.set('role', params.role);
      const suffix = qs.toString() ? `?${qs.toString()}` : '';
      return api.get<{ users: PlatformUser[]; total: number }>(`/api/platform/users${suffix}`);
    },
  });
}

export function useUpdateUserRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; platform_role: 'owner' | 'user' }) =>
      api.patch<{ updated: boolean }>(`/api/platform/users/${args.id}`, {
        platform_role: args.platform_role,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'users'] }),
  });
}

// ---------- Orgs ----------

export type PlatformOrg = {
  id: string;
  name: string;
  slug: string;
  credit_balance: number;
  current_package_id: string | null;
  package_expires_at: number | null;
  trial_ends_at: number | null;
  created_at: number;
  owner_email: string | null;
  owner_name: string | null;
  member_count: number;
  site_count: number;
};

export function usePlatformOrgs() {
  return useQuery({
    queryKey: ['platform', 'orgs'],
    queryFn: () => api.get<{ organizations: PlatformOrg[] }>('/api/platform/orgs'),
  });
}

export function useGrantCredits() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { org_id: string; amount: number; note?: string }) =>
      api.post<{ balance: number; amount: number }>(
        `/api/platform/orgs/${args.org_id}/grant-credits`,
        { amount: args.amount, note: args.note },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'orgs'] }),
  });
}

// ---------- AI Providers ----------

export type AIProviderStatus = {
  provider: 'aimlapi' | 'openai' | 'anthropic' | 'gemini';
  configured: boolean;
  enabled: boolean;
  key_hint: string | null;
  updated_at: number | null;
};

export type AIProviderCatalogEntry = {
  id: 'aimlapi' | 'openai' | 'anthropic' | 'gemini';
  label: string;
  description: string;
  is_default?: boolean;
  key_placeholder: string;
  docs_url: string;
};

export function useAIProviders() {
  return useQuery({
    queryKey: ['platform', 'ai-providers'],
    queryFn: () =>
      api.get<{ providers: AIProviderStatus[]; catalog: AIProviderCatalogEntry[] }>(
        '/api/platform/settings/ai-providers',
      ),
  });
}

export function useSaveAIProvider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      provider: 'aimlapi' | 'openai' | 'anthropic' | 'gemini';
      key: string;
      label?: string;
    }) =>
      api.put<{ provider: string; configured: boolean }>(
        `/api/platform/settings/ai-providers/${args.provider}`,
        { key: args.key, label: args.label },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'ai-providers'] }),
  });
}

export function useRemoveAIProvider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (provider: 'aimlapi' | 'openai' | 'anthropic' | 'gemini') =>
      api.del<{ removed: boolean }>(`/api/platform/settings/ai-providers/${provider}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'ai-providers'] }),
  });
}

export function useTestAIProvider() {
  return useMutation({
    mutationFn: (args: {
      provider: 'aimlapi' | 'openai' | 'anthropic' | 'gemini';
      key?: string;
    }) =>
      api.post<{ ok: boolean; message: string; details?: Record<string, unknown> }>(
        `/api/platform/settings/ai-providers/${args.provider}/test`,
        { key: args.key },
      ),
  });
}

// ---------- Metrics ----------

export type PlatformMetrics = {
  users: number;
  organizations: number;
  owners: number;
  memberships: number;
  sites: number;
  content: number;
  credits: { granted: number; consumed: number; outstanding_balance: number };
};

export function usePlatformMetrics() {
  return useQuery({
    queryKey: ['platform', 'metrics'],
    queryFn: () => api.get<PlatformMetrics>('/api/platform/metrics'),
    refetchInterval: 60_000,
  });
}
